import defaultStyles from "./Input/Input.module.scss";
import newStyles from "../../../revamp/sections/HomeNewFaq/Input/Input.module.scss";
import cn from "clsx";
import { useEffect, type MouseEvent } from "react";
import { clearSubmitBusyDom } from "@/helpers/paintFirst";
import {
  triggerInpSafeFormSubmit,
  useInpSafeFormSubmit,
} from "@/helpers/inpSafeForm";

export default function SubmitButton({
  isDisabled = false,
  text = "Submit",
  bgColor = "#1a325d",
  textColor = "#fff",
  useNewStyle = false,
  borderColor = textColor,
  icon,
  id,
  className,
  name = "submit_btn",
  value = "submit_btn",
  isProgramListPage = false,
  isMobile = false,
  isLoading = false,
  onPress,
}: {
  isDisabled?: boolean;
  text?: string;
  bgColor?: string;
  textColor?: string;
  useNewStyle?: boolean;
  borderColor?: string;
  id?: string;
  icon?: React.ReactNode;
  className?: string;
  name?: string;
  value?: string;
  isProgramListPage?: boolean;
  isMobile?: boolean;
  /** When true, shows spinner beside `text` and sets aria-busy. */
  isLoading?: boolean;
  /** Optional explicit handler; otherwise uses InpSafeForm registry on parent form. */
  onPress?: (e: MouseEvent<HTMLButtonElement>) => void;
}) {
  const styles = useNewStyle ? newStyles : defaultStyles;
  const busy = isLoading || isDisabled;
  const inpSafeSubmit = useInpSafeFormSubmit();

  useEffect(() => {
    if (!isLoading) {
      clearSubmitBusyDom();
    }
  }, [isLoading]);

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    if (onPress) {
      onPress(e);
      return;
    }
    if (inpSafeSubmit) {
      inpSafeSubmit(e);
      return;
    }
    triggerInpSafeFormSubmit(e.currentTarget, e);
  };

  return (
    <button
      id={id || "submit-button"}
      type="button"
      disabled={isDisabled}
      aria-busy={isLoading || undefined}
      onClick={handleClick}
      className={cn(
        "font-semibold font-raleway w-full border",
        styles.btn,
        className,
        {
          ["bg-[#E7E7E7] border-stone-500 text-stone-500"]: isDisabled && !isProgramListPage,
          ["bg-primary-blue border-blue-950 text-white"]:
            !isDisabled && !isProgramListPage,
          ["bg-[#FFCB05] border-blue-950 text-[#1B294B]"]:
            !isDisabled && isProgramListPage,
        },
      )}
      style={
        isDisabled
          ? {
              borderRadius: isProgramListPage ? "0.62rem" : undefined,
              fontSize:
                isMobile && isProgramListPage
                  ? "0.875rem"
                  : isProgramListPage
                    ? "1.25rem"
                    : undefined,
              fontWeight: isProgramListPage ? 700 : undefined,
              padding: isMobile && isProgramListPage ? "0.5rem 0" : undefined,
            }
          : {
              background: isProgramListPage ? "#FFCB05" : bgColor,
              color: isProgramListPage ? "#1B294B" : textColor,
              borderColor: isProgramListPage ? "#1A325D" : borderColor,
              borderRadius: isProgramListPage ? "0.62rem" : undefined,
              fontSize:
                isMobile && isProgramListPage
                  ? "0.875rem"
                  : isProgramListPage
                    ? "1.25rem"
                    : undefined,
              fontWeight: isProgramListPage ? 700 : undefined,
              padding: isMobile && isProgramListPage ? "0.5rem 0" : undefined,
            }
      }
      name={name}
      value={value}
    >
      <span className={cn(styles.btnContent, "flex items-center justify-center")}>
        <span
          className={cn(styles.btnSpinner, !isLoading && styles.btnSpinnerHidden)}
          aria-hidden={!isLoading}
        />
        {icon && !isLoading && <span className="mr-2 inline-block">{icon}</span>}
        <span>{text}</span>
      </span>
    </button>
  );
}
